/**
 * Shared formatting utilities for dates, times, and money.
 * All money values in the system are stored as INTEGER cents.
 * This is the ONLY place cents-to-display or date-to-display conversion should happen.
 *
 * TIMEZONE: All date/time display functions use America/Chicago (US Central).
 * This is a single-company app in Kansas City — hardcoding Central is correct.
 */

import { CENTRAL_TIME_ZONE } from './platform/date';

export { getLocalToday, toLocalDateStr } from './platform/date';

const TZ = CENTRAL_TIME_ZONE;

/**
 * Format cents as a currency string with dollar sign.
 * @param cents - Integer cents value (e.g., 2999 -> "$29.99")
 * @param options - Optional formatting configuration
 */
export function formatCents(
  cents: number | null | undefined,
  options?: {
    decimals?: number;       // decimal places (default: auto — 0 for whole dollars, 2 otherwise)
    locale?: boolean;        // use locale formatting with commas (default: false)
    accounting?: boolean;    // parentheses for negatives (default: false)
  }
): string {
  if (cents === null || cents === undefined) return '\u2014'; // em dash

  // Auto-detect: whole dollar amounts drop .00, fractional keep 2 decimals
  const decimals = options?.decimals ?? (Math.round(cents) % 100 === 0 ? 0 : 2);
  const useLocale = options?.locale ?? false;
  const accounting = options?.accounting ?? false;

  const dollars = Math.abs(cents) / 100;
  const isNegative = cents < 0;

  let formatted: string;
  if (useLocale) {
    formatted = dollars.toLocaleString('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  } else {
    formatted = dollars.toFixed(decimals);
  }

  if (isNegative) {
    return accounting ? `($${formatted})` : `-$${formatted}`;
  }
  return `$${formatted}`;
}

// ---------------------------------------------------------------------------
// Date formatting
// ---------------------------------------------------------------------------
/**
 * Normalize a stored date-ish string into a form every JS engine (including
 * mobile Safari) can parse, preserving the project's date-only convention:
 * - `YYYY-MM-DD` (date-only) → `${value}T12:00:00` (noon avoids the UTC-midnight
 *   day-shift when rendered in Central time).
 * - Postgres text timestamps (`2026-09-04 18:34:51.065582+00`, space-separated,
 *   short UTC offset) → strict ISO (`2026-09-04T18:34:51.065582+00:00`).
 * - Anything else → trimmed as-is.
 * Returns null for nullish/blank input.
 */
const PG_TEXT_TIMESTAMP = /^(\d{4}-\d{2}-\d{2}) (\d{2}:\d{2}:\d{2}(?:\.\d+)?)(Z|[+-]\d{2}(?::?\d{2})?)$/;
const PG_TEXT_TIMESTAMP_NO_TZ = /^(\d{4}-\d{2}-\d{2}) (\d{2}:\d{2}:\d{2}(?:\.\d+)?)$/;

export function toDisplayDateInput(value: string | null | undefined): string | null {
    if (value == null) return null;
    const trimmed = String(value).trim();
    if (!trimmed) return null;
    const withTz = PG_TEXT_TIMESTAMP.exec(trimmed);
    if (withTz) {
        const [, date, time, offset] = withTz;
        let suffix = offset;
        if (/^[+-]\d{2}$/.test(suffix)) suffix += ':00';
        else if (/^[+-]\d{4}$/.test(suffix)) suffix = `${suffix.slice(0, 3)}:${suffix.slice(3)}`;
        return `${date}T${time}${suffix}`;
    }
    const noTz = PG_TEXT_TIMESTAMP_NO_TZ.exec(trimmed);
    if (noTz) return `${noTz[1]}T${noTz[2]}`;
    return /^\d{4}-\d{2}-\d{2}$/.test(trimmed) ? `${trimmed}T12:00:00` : trimmed;
}


/**
 * Format a date for display.
 * @param date - Date string, Date object, or nullish value
 * @param style - 'short' ("Mar 12"), 'medium' ("Mar 12, 2026"), 'long' ("March 12, 2026")
 */
export function formatDate(
    date: string | Date | null | undefined,
    style: 'short' | 'medium' | 'long' = 'medium',
): string {
    if (!date) return '\u2014';
    const d = new Date(typeof date === 'string' ? (toDisplayDateInput(date) ?? '') : date);
    if (isNaN(d.getTime())) return '\u2014';
    switch (style) {
        case 'short':
            return d.toLocaleDateString('en-US', { timeZone: TZ, month: 'short', day: 'numeric' });
        case 'medium':
            return d.toLocaleDateString('en-US', { timeZone: TZ, month: 'short', day: 'numeric', year: 'numeric' });
        case 'long':
            return d.toLocaleDateString('en-US', { timeZone: TZ, month: 'long', day: 'numeric', year: 'numeric' });
    }
}

/**
 * Format a date with time: "Mar 12, 2026, 3:45 PM"
 */
export function formatDateTime(date: string | Date | null | undefined): string {
    if (!date) return '\u2014';
    const d = new Date(date);
    if (isNaN(d.getTime())) return '\u2014';
    return d.toLocaleDateString('en-US', {
        timeZone: TZ,
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
    });
}

/**
 * Format time only: "3:45 PM"
 */
export function formatTime(date: string | Date | null | undefined): string {
    if (!date) return '\u2014';
    const d = new Date(date);
    if (isNaN(d.getTime())) return '\u2014';
    return d.toLocaleTimeString('en-US', { timeZone: TZ, hour: 'numeric', minute: '2-digit' });
}

/**
 * Format a date as a relative time string: "just now", "5m ago", "3h ago", "2d ago".
 * Falls back to medium date format after 7 days.
 */
export function formatRelative(date: string | Date | null | undefined): string {
    if (!date) return '\u2014';
    const d = new Date(date);
    if (isNaN(d.getTime())) return '\u2014';
    const diff = Date.now() - d.getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d ago`;
    return formatDate(d, 'medium');
}

/**
 * Format an epoch-ms timestamp as time of day: "3:45 PM"
 * Useful for time-tracking clocks and timers that store epoch-ms.
 */
export function formatTimeOfDay(epochMs: number | null | undefined): string {
    if (!epochMs || isNaN(epochMs)) return '\u2014';
    const d = new Date(epochMs);
    if (isNaN(d.getTime())) return '\u2014';
    return d.toLocaleTimeString('en-US', { timeZone: TZ, hour: 'numeric', minute: '2-digit' });
}

/**
 * Format a date for chat / conversation UIs: "Today", "Yesterday", or "Mar 12".
 */
export function formatDateChat(date: string | Date | null | undefined): string {
    if (!date) return '\u2014';
    const d = new Date(date);
    if (isNaN(d.getTime())) return '\u2014';
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    if (d.toDateString() === today.toDateString()) return 'Today';
    if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
    return d.toLocaleDateString('en-US', { timeZone: TZ, month: 'short', day: 'numeric' });
}

/**
 * Format an arrival/departure pair as a time window: "3:45 PM - 4:15 PM" or "3:45 PM".
 */
export function formatTimeWindow(
    arrival: string | null | undefined,
    departure: string | null | undefined,
): string {
    if (!arrival) return '';
    const fmt = (iso: string) => {
        const d = new Date(iso);
        if (isNaN(d.getTime())) return iso;
        return d.toLocaleTimeString('en-US', { timeZone: TZ, hour: 'numeric', minute: '2-digit' });
    };
    if (departure) return `${fmt(arrival)} - ${fmt(departure)}`;
    return fmt(arrival);
}

/**
 * Format a date with weekday: "Tue, Mar 12" (short) or "Tuesday, March 12, 2026" (long).
 */
export function formatDateWithWeekday(
    date: string | Date | null | undefined,
    style: 'short' | 'long' = 'short',
): string {
    if (!date) return '\u2014';
    const d = new Date(date);
    if (isNaN(d.getTime())) return '\u2014';
    if (style === 'short') {
        return d.toLocaleDateString('en-US', { timeZone: TZ, weekday: 'short', month: 'short', day: 'numeric' });
    }
    return d.toLocaleDateString('en-US', {
        timeZone: TZ, weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
    });
}

/**
 * Format a date-only calendar value without interpreting it as UTC midnight.
 * Noon UTC stays on the same calendar day in America/Chicago throughout the
 * year, including daylight-saving transitions.
 */
export function formatCalendarDateWithWeekday(
    date: string | null | undefined,
    style: 'short' | 'long' = 'short',
): string {
    if (!date) return '\u2014';
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return '\u2014';
    const instant = new Date(`${date}T12:00:00Z`);
    if (isNaN(instant.getTime()) || instant.toISOString().slice(0, 10) !== date) return '\u2014';
    return formatDateWithWeekday(instant, style);
}

/**
 * Format a date as month + year: "Mar 2026".
 */
export function formatMonthYear(date: string | Date | null | undefined): string {
    if (!date) return '\u2014';
    const d = new Date(typeof date === 'string' ? (toDisplayDateInput(date) ?? '') : date);
    if (isNaN(d.getTime())) return '\u2014';
    return d.toLocaleDateString('en-US', { timeZone: TZ, month: 'short', year: 'numeric' });
}

/**
 * Format a date range: "Mar 12, 2026 – Mar 15, 2026".
 * Appends 'T12:00:00' to plain date strings to avoid UTC boundary issues.
 */
export function formatDateRange(start: string, end: string): string {
    const s = new Date(toDisplayDateInput(start) ?? '');
    const e = new Date(toDisplayDateInput(end) ?? '');
    return `${formatDate(s, 'medium')} – ${formatDate(e, 'medium')}`;
}

/**
 * Coerce a stored date-ish string to a bare YYYY-MM-DD.
 *
 * Several date columns are TEXT (contracts.start_date, estimates.contract_start,
 * recurring_billing_rules.next_run_date), so nothing stops a timestamptz-shaped
 * value like '2026-04-01 00:00:00+00' from landing in a field that is meant to
 * hold a date. Downstream parsers expect a bare date; a stray time component has
 * already silently bricked customer billing once. Normalize at every write.
 *
 * Returns null for empty/unparseable input so the caller can fall back.
 */
export function toDateOnly(value: string | null | undefined): string | null {
  if (!value) return null;
  const match = /^(\d{4}-\d{2}-\d{2})(?:[T ].*)?$/.exec(String(value).trim());
  return match ? match[1] : null;
}

/**
 * Convert any Date to "h:mm AM/PM" in US Central time (server-side).
 */
export function toLocalTimeStr(d: Date): string {
  return new Intl.DateTimeFormat('en-US', {
    timeZone: TZ,
    hour: 'numeric',
    minute: '2-digit',
  }).format(d);
}

/**
 * Format cents as a numeric string WITHOUT a dollar sign.
 * Useful for input fields, CSV exports, and contexts where the "$" is in the label.
 * @param cents - Integer cents value (e.g., 2999 -> "29.99")
 * @param options - Optional formatting configuration
 */
export function formatCentsRaw(
  cents: number | null | undefined,
  options?: {
    decimals?: number;       // decimal places (default: auto — 0 for whole dollars, 2 otherwise)
    locale?: boolean;        // use locale formatting with commas (default: false)
  }
): string {
  if (cents === null || cents === undefined) return '0';

  const decimals = options?.decimals ?? (Math.round(cents) % 100 === 0 ? 0 : 2);
  const useLocale = options?.locale ?? false;
  const dollars = Math.abs(cents) / 100;

  if (useLocale) {
    return dollars.toLocaleString('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  }
  return dollars.toFixed(decimals);
}

// ---------------------------------------------------------------------------
// Additional currency helpers
// ---------------------------------------------------------------------------

/**
 * Format cents using Intl currency formatting.
 * Auto-detects: whole dollar amounts show "$169", fractional show "$169.15".
 */
export function formatCurrency(cents: number | null | undefined): string {
    if (cents == null) return '$0';
    const decimals = Math.round(cents) % 100 === 0 ? 0 : 2;
    return (cents / 100).toLocaleString('en-US', {
        style: 'currency', currency: 'USD',
        minimumFractionDigits: decimals, maximumFractionDigits: decimals,
    });
}

/**
 * Compact currency display for large values: "$1.2M", "$45.3K", or "$29.99"
 */
export function formatCurrencyShort(cents: number | null | undefined): string {
    if (cents == null) return '$0';
    const dollars = Math.abs(cents / 100);
    const sign = cents < 0 ? '-' : '';
    if (dollars >= 1_000_000) return `${sign}$${(dollars / 1_000_000).toFixed(1)}M`;
    if (dollars >= 1_000) return `${sign}$${(dollars / 1_000).toFixed(1)}K`;
    return formatCurrency(cents);
}
